import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Form, Overlay, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { MdCheck, MdFlag } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { SectionStatus, SectionStatusType } from '../data-qa-constants';
import { DiffSummary } from '../data-qa-types';
import { changeSummary } from './review-bar-utils';
import { useReviewNotice } from './ReviewNoticeContext';
import { ReviewControlsSlotContext } from './ReviewControlsSlot';
import { useCommentsContext } from './CommentsContext';

interface ReviewControlsProps {
  status: SectionStatusType;
  notes: string;
  pending: boolean;
  onApprove: () => void;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
  // e.g. 'Approve Media Inputs' / 'Flag Media Inputs' -- the buttons' tooltips.
  approveLabel: string;
  flagLabel: string;
  // When set, Approve is disabled with this as its tooltip -- a blocking data problem
  // (e.g. negative values) the reviewer can't approve past. Flag still works.
  approveBlockedReason?: string;
  // This section's fingerprint diff against its last review.
  summary: DiffSummary;
  isColdStart: boolean;
  reviewed: number;
  total: number;
  unit?: string;
}

// The active tab's review controls: how much of it is reviewed, then Flag and Approve as
// icon buttons. They render into the sticky step bar (ReviewControlsSlotContext) so they
// stay in view; the step's own badge above already says whether it's approved or flagged,
// and the buttons fill in to match.
export const ReviewControls = (props: ReviewControlsProps) => {
  const slot = useContext(ReviewControlsSlotContext);
  const controls = <ReviewControlsRow {...props} />;
  if (slot) return createPortal(controls, slot);
  return <div className='data-qa-review-controls-inline'>{controls}</div>;
};

const ReviewControlsRow = ({
  status,
  notes,
  pending,
  onApprove,
  onFlag,
  onNoteChange,
  approveLabel,
  flagLabel,
  approveBlockedReason,
  summary,
  isColdStart,
  reviewed,
  total,
  unit = 'weeks'
}: ReviewControlsProps) => {
  // The cross-model notice itself is in the section header; here it only says who
  // else an approval carries over to.
  const notice = useReviewNotice();
  const isApproved = status === SectionStatus.APPROVED;
  const isFlagged = status === SectionStatus.FLAGGED;
  let approveTooltip = approveBlockedReason ?? approveLabel;
  if (!approveBlockedReason && notice?.approveHint) {
    approveTooltip = `${approveLabel}. ${notice.approveHint}`;
  }

  return (
    <div className='data-qa-review-controls'>
      <OverlayTrigger
        placement='bottom'
        overlay={<Tooltip>{changeSummary(summary, isColdStart)}</Tooltip>}
      >
        <span className='data-qa-review-controls__progress'>
          {reviewed}/{total} {unit} reviewed
          {/* The tooltip's text, for screen readers. */}
          <span className='visually-hidden'>. {changeSummary(summary, isColdStart)}</span>
        </span>
      </OverlayTrigger>
      <FlagControl
        isFlagged={isFlagged}
        notes={notes}
        pending={pending}
        flagLabel={flagLabel}
        onFlag={onFlag}
        onNoteChange={onNoteChange}
      />
      <IconButton
        size={Size.MD}
        design={Intent.SUCCESS}
        appearance={isApproved ? Appearance.FILLED : Appearance.OUTLINED}
        aria-label={approveLabel}
        aria-pressed={isApproved}
        loading={pending && !isApproved}
        disabled={Boolean(approveBlockedReason)}
        tooltip={{ text: isApproved ? 'Approved' : approveTooltip }}
        onClick={onApprove}
      >
        <MdCheck />
      </IconButton>
    </div>
  );
};

interface FlagControlProps {
  isFlagged: boolean;
  notes: string;
  pending: boolean;
  flagLabel: string;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
}

// The flag icon asks for the flag's reason in the tab's comment thread (CommentsContext);
// outside the page, where there's no thread, in a note popover instead.
const FlagControl = ({
  isFlagged,
  notes,
  pending,
  flagLabel,
  onFlag,
  onNoteChange
}: FlagControlProps) => {
  // State, not a ref: the popover anchors to the button once it's mounted.
  const [target, setTarget] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const comments = useCommentsContext();

  const onClick = () => {
    if (!comments) {
      setOpen(o => !o);
      return;
    }
    comments.requestFlag({
      label: flagLabel,
      notes,
      onSubmit: reason => {
        onNoteChange(reason);
        onFlag(reason);
      }
    });
  };

  const submit = () => {
    onFlag(notes);
    setOpen(false);
  };

  return (
    <>
      <IconButton
        ref={setTarget}
        size={Size.MD}
        design={Intent.WARNING}
        appearance={isFlagged ? Appearance.FILLED : Appearance.OUTLINED}
        aria-label={flagLabel}
        aria-pressed={isFlagged}
        aria-expanded={open}
        tooltip={
          open ? undefined : { text: isFlagged ? 'Flagged: view or update the note' : flagLabel }
        }
        onClick={onClick}
      >
        <MdFlag />
      </IconButton>
      <Overlay
        target={target}
        show={open}
        placement='bottom-end'
        rootClose
        onHide={() => setOpen(false)}
      >
        <Popover className='data-qa-flag-popover'>
          <Popover.Body>
            <Form.Control
              as='textarea'
              rows={3}
              autoFocus
              aria-label='Flag note'
              placeholder='What needs attention? (required to flag)'
              value={notes}
              onChange={e => onNoteChange(e.target.value)}
            />
            <div className='data-qa-flag-popover__actions'>
              <Button
                size={Size.SM}
                appearance={Appearance.OUTLINED}
                design={Design.NEUTRAL}
                onClick={() => setOpen(false)}
              >
                {isFlagged ? 'Close' : 'Cancel'}
              </Button>
              <Button
                size={Size.SM}
                design={Intent.WARNING}
                leadingElement={<MdFlag />}
                loading={pending}
                disabled={notes.trim().length === 0}
                onClick={submit}
              >
                {isFlagged ? 'Update flag' : flagLabel}
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};
